import { l } from '@manychat/manyui'

import {
  ExternalTriggerType,
  HotmartPostPurchaseStatus,
  HotmartPostPurchaseStatusLabels,
  HotmartPurchaseProductCondition,
} from 'apps/externalTriggers/externalTriggerConstants'
import { IExternalTrigger } from 'apps/externalTriggers/externalTriggerInterfaces'
import { HotmartProductSchemaType } from 'shared/api/requests/integrations/hotmart'

export const getPostPuchaseChangeTriggerStatus = (
  trigger: Partial<IExternalTrigger>,
): HotmartPostPurchaseStatus | undefined => {
  if (
    trigger.externalTriggerType !== ExternalTriggerType.HOTMART_POST_PURCHASE_CHANGE_STATUS ||
    !trigger.data ||
    !('status' in trigger.data)
  ) {
    return
  }

  return trigger.data.status
}

const isHotmartProductTrigger = (trigger: Partial<IExternalTrigger>): boolean => {
  return (
    trigger.externalTriggerType === ExternalTriggerType.HOTMART_POST_PURCHASE_CHANGE_STATUS ||
    trigger.externalTriggerType === ExternalTriggerType.HOTMART_ABANDONED_CART
  )
}

export const getPostPuchaseChangeTriggerProductCondition = (
  trigger: Partial<IExternalTrigger>,
): HotmartPurchaseProductCondition => {
  if (
    !isHotmartProductTrigger(trigger) ||
    !trigger.data ||
    !('product_condition' in trigger.data)
  ) {
    return HotmartPurchaseProductCondition.ANY
  }

  return trigger.data.product_condition || HotmartPurchaseProductCondition.ANY
}

export const getPostPuchaseChangeTriggerProducts = (trigger: Partial<IExternalTrigger>) => {
  if (!isHotmartProductTrigger(trigger) || !trigger.data || !('product_ids' in trigger.data)) {
    return []
  }

  return trigger.data.product_ids || []
}

export const getHotmartTriggerOneProductData = (
  trigger: Partial<IExternalTrigger>,
): HotmartProductSchemaType | undefined => {
  if (!isHotmartProductTrigger(trigger) || !trigger.data || !('one_product_data' in trigger.data)) {
    return undefined
  }

  return trigger.data.one_product_data
}

export const getHotmartTriggerProductCaption = (
  trigger: Partial<IExternalTrigger>,
): string | undefined => {
  if (!isHotmartProductTrigger(trigger)) {
    return
  }

  const condition = getPostPuchaseChangeTriggerProductCondition(trigger)

  if (condition === HotmartPurchaseProductCondition.ANY) {
    return l.translate('any product')
  }

  const selectedProducts = getPostPuchaseChangeTriggerProducts(trigger)

  if (selectedProducts.length === 1) {
    const oneProductData = getHotmartTriggerOneProductData(trigger)
    if (oneProductData) {
      return oneProductData.name
    }
  }

  return l.translate('specific products')
}

export const getHotmartPurchaseChangeTriggerCaption = (
  trigger: Partial<IExternalTrigger>,
): string => {
  const triggerStatus = getPostPuchaseChangeTriggerStatus(trigger)

  const statusCaption = triggerStatus
    ? l.getString(HotmartPostPurchaseStatusLabels[triggerStatus])
    : l.translate('Unknown status')
  const productCaption = getHotmartTriggerProductCaption(trigger) || l.translate('product')

  return l.translate('{status} for {product}', { status: statusCaption, product: productCaption })
}

export const getHotmartAbandonedCartTriggerCaption = (
  trigger: Partial<IExternalTrigger>,
  isFullCaption = false,
): string => {
  const productCaption = getHotmartTriggerProductCaption(trigger) || l.translate('product')
  const textData = { product: productCaption }

  return isFullCaption
    ? l.translate('Customer abandons their cart with {product}', textData)
    : l.translate('With {product}', textData)
}
